import { getDenomination, isForexAssetType } from "./Charting/utils";

const DEFAULT_PRICE_FORMATTER = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});
const FOREX_PRICE_FORMATTER = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 5,
  maximumFractionDigits: 5,
});
export const ONE_DAY_MS = 24 * 60 * 60 * 1000;
export const TRADE_STATS_WINDOWS = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last month" },
  { value: "all", label: "All time" },
];
export const TRADE_NOTICE_STORAGE_KEY = "warp:trade-proposal-notices:v1";
export const RECENT_TRADE_EVENT_WINDOW_MS = 7 * ONE_DAY_MS;

export const toNonEmptyString = (value) => {
  if (value == null) return null;
  const str = String(value).trim();
  return str.length ? str : null;
};

export const formatDuration = (value) => {
  const str = toNonEmptyString(value);
  if (!str) return null;
  const normalized = str.replace(/[_-]+/g, " ");
  return normalized.replace(/\b\w/g, (c) => c.toUpperCase());
};

export const formatPrice = (value, assetType) => {
  if (value == null || value === "") return null;
  const num = typeof value === "number" ? value : Number(value);
  const denom = getDenomination(assetType);
  if (Number.isFinite(num)) {
    const formatter = isForexAssetType(assetType)
      ? FOREX_PRICE_FORMATTER
      : DEFAULT_PRICE_FORMATTER;
    return `${denom}${formatter.format(num)}`;
  }
  return toNonEmptyString(value);
};

export const formatPercent = (value) => {
  if (value == null || value === "") return null;
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return null;
  return `${num.toFixed(2)}%`;
};

export const formatSignedPercent = (value) => {
  if (value == null || value === "") return null;
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return null;
  const base = `${num.toFixed(2)}%`;
  return num > 0 ? `+${base}` : base;
};

export const formatDate = (value, tz) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: tz || Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
};

export const toTradeNumber = (value) => {
  if (value == null || value === "") return null;
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) ? num : null;
};

export const getProposalEntryPrice = (proposal) =>
  toTradeNumber(proposal?.entryPrice ?? proposal?.entry_price);

export const getProposalStopLoss = (proposal) =>
  toTradeNumber(proposal?.stopLoss ?? proposal?.stop_loss);

export const getProposalTarget = (proposal) =>
  toTradeNumber(proposal?.priceTarget ?? proposal?.price_target);

export const inferTradeSide = (proposal) => {
  const entry = getProposalEntryPrice(proposal);
  if (entry == null) return null;

  const stop = getProposalStopLoss(proposal);
  if (stop != null && stop !== entry) {
    if (stop < entry) return "long";
    if (stop > entry) return "short";
  }

  const target = getProposalTarget(proposal);
  if (target != null && target !== entry) {
    if (target > entry) return "long";
    if (target < entry) return "short";
  }

  return null;
};

export const computeDirectionalPercentChange = (entryPrice, endPrice, tradeSide) => {
  if (!Number.isFinite(entryPrice) || entryPrice === 0 || !Number.isFinite(endPrice)) {
    return null;
  }

  const raw = ((endPrice - entryPrice) / entryPrice) * 100;
  return tradeSide === "short" ? -raw : raw;
};

export const getNormalizedStatus = (proposal) =>
  toNonEmptyString(proposal?.status)?.toLowerCase();

export const getNormalizedExitReason = (proposal) =>
  toNonEmptyString(proposal?.exitReason ?? proposal?.exit_reason)?.toLowerCase();

export const getProposalPercentChange = (proposal) => {
  const status = getNormalizedStatus(proposal);
  if (status === "pending_activation") return null;

  const exitReason = getNormalizedExitReason(proposal);
  const tradeSide = inferTradeSide(proposal);
  const entry = getProposalEntryPrice(proposal);

  if (status === "closed" && tradeSide && Number.isFinite(entry)) {
    if (exitReason === "profit_target") {
      const target = getProposalTarget(proposal);
      const realized = computeDirectionalPercentChange(entry, target, tradeSide);
      if (realized != null) return realized;
    }

    if (exitReason === "stop_loss") {
      const stop = getProposalStopLoss(proposal);
      const realized = computeDirectionalPercentChange(entry, stop, tradeSide);
      if (realized != null) return realized;
    }
  }

  return toTradeNumber(
    proposal?.percentChangeFromEntry ?? proposal?.percent_change_from_entry,
  );
};

export const formatPromptEntryPrice = (value, assetType) => {
  if (value == null || value === "") return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: isForexAssetType(assetType) ? 5 : 4,
  });
};

export const formatStatusLabel = (status) => {
  const normalized = toNonEmptyString(status)?.toLowerCase();
  if (normalized === "pending_activation") return "Pending";
  return null;
};

export const getPendingActivationCondition = (proposal, assetType) => {
  const status = getNormalizedStatus(proposal);
  if (status !== "pending_activation") return null;

  const entry = getProposalEntryPrice(proposal);
  const proposedAt = toTradeNumber(
    proposal?.livePriceAtSuggested ?? proposal?.live_price_at_suggested,
  );
  const formattedEntry = formatPrice(
    proposal?.entryPrice ?? proposal?.entry_price,
    assetType,
  );

  if (!Number.isFinite(entry) || !Number.isFinite(proposedAt)) {
    return formattedEntry ? `Touch ${formattedEntry}` : "Touch entry";
  }

  if (Math.abs(entry - proposedAt) <= 0.01) {
    return formattedEntry ? `Touch ${formattedEntry}` : "Touch entry";
  }

  const comparator = proposedAt < entry ? ">=" : "<=";
  return formattedEntry ? `${comparator} ${formattedEntry}` : `Touch ${comparator} entry`;
};

export const getStatusSortOrder = (proposal) => {
  const status = getNormalizedStatus(proposal);
  if (status === "pending_activation") return 0;
  if (status === "active") return 1;
  if (status === "closed") return 2;
  return 3;
};

export const getClosedTradeOutcome = (proposal) => {
  const status = getNormalizedStatus(proposal);
  if (status !== "closed") return null;

  const exitReason = getNormalizedExitReason(proposal);
  if (exitReason === "profit_target") return "win";
  if (exitReason === "stop_loss") return "loss";

  const change = getProposalPercentChange(proposal);
  if (change != null) {
    if (change > 0) return "win";
    if (change < 0) return "loss";
  }

  return null;
};

export const getClosedExitValue = (proposal, assetType) => {
  const exitReason = getNormalizedExitReason(proposal);

  if (exitReason === "profit_target") {
    return (
      formatPrice(proposal?.priceTarget ?? proposal?.price_target, assetType) ||
      formatPrice(proposal?.currentPrice ?? proposal?.current_price, assetType)
    );
  }

  if (exitReason === "stop_loss") {
    return (
      formatPrice(proposal?.stopLoss ?? proposal?.stop_loss, assetType) ||
      formatPrice(proposal?.currentPrice ?? proposal?.current_price, assetType)
    );
  }

  return formatPrice(proposal?.currentPrice ?? proposal?.current_price, assetType);
};

export const getProposalSortTimestamp = (proposal) => {
  const createdAt = Date.parse(
    toNonEmptyString(proposal?.createdAt ?? proposal?.created_at) || "",
  );
  if (Number.isFinite(createdAt)) return createdAt;

  const updatedAt = Date.parse(
    toNonEmptyString(proposal?.updatedAt ?? proposal?.updated_at) || "",
  );
  return Number.isFinite(updatedAt) ? updatedAt : 0;
};

export const getProposalClosedTimestamp = (proposal) => {
  const updatedAt = Date.parse(
    toNonEmptyString(proposal?.updatedAt ?? proposal?.updated_at) || "",
  );
  if (Number.isFinite(updatedAt)) return updatedAt;

  const createdAt = Date.parse(
    toNonEmptyString(proposal?.createdAt ?? proposal?.created_at) || "",
  );
  return Number.isFinite(createdAt) ? createdAt : NaN;
};

export const getLatestEventKind = (proposal) =>
  toNonEmptyString(proposal?.latestEventKind ?? proposal?.latest_event_kind)?.toLowerCase();

export const getLatestEventAtIso = (proposal) =>
  toNonEmptyString(proposal?.latestEventAt ?? proposal?.latest_event_at);

export const getLatestEventAtMs = (proposal) => {
  const raw = getLatestEventAtIso(proposal);
  if (!raw) return NaN;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : NaN;
};

export const isRecentTradeEvent = (proposal) => {
  const eventAtMs = getLatestEventAtMs(proposal);
  if (!Number.isFinite(eventAtMs)) return false;
  return (Date.now() - eventAtMs) <= RECENT_TRADE_EVENT_WINDOW_MS;
};

export const getTradeEventLabel = (eventKind) => {
  switch (eventKind) {
    case "activated":
      return "Activated";
    case "profit_target_hit":
      return "Target hit";
    case "stop_loss_hit":
      return "Stop loss hit";
    default:
      return null;
  }
};

export const getTradeEventSeverity = (eventKind) => {
  switch (eventKind) {
    case "profit_target_hit":
      return "success";
    case "stop_loss_hit":
      return "warning";
    default:
      return "info";
  }
};

export const buildTradeNoticeKey = ({ proposalId, eventKind, eventAt }) => {
  if (proposalId == null || !eventKind || !eventAt) return null;
  return `${proposalId}:${eventKind}:${eventAt}`;
};

export const buildTradeNoticeFromProposal = (proposal) => {
  const eventKind = getLatestEventKind(proposal);
  const eventAt = getLatestEventAtIso(proposal);
  const label = getTradeEventLabel(eventKind);
  const ticker = proposal?.ticker || proposal?.name || "Trade";
  if (!eventKind || !eventAt || !label) return null;

  let message = `${ticker} update available.`;
  if (eventKind === "activated") {
    message = `${ticker} moved from pending to active.`;
  } else if (eventKind === "profit_target_hit") {
    message = `${ticker} hit profit target and was closed.`;
  } else if (eventKind === "stop_loss_hit") {
    message = `${ticker} hit stop loss and was closed.`;
  }

  return {
    key: buildTradeNoticeKey({
      proposalId: proposal?.id,
      eventKind,
      eventAt,
    }),
    proposalId: proposal?.id,
    chatMessageId: proposal?.chatMessageId ?? proposal?.chat_message_id,
    eventKind,
    title: label,
    message,
    severity: getTradeEventSeverity(eventKind),
    eventAtMs: getLatestEventAtMs(proposal),
  };
};

export const buildTradeNoticeFromEvent = (detail) => {
  if (!detail?.tradeProposalId || !detail?.event || !detail?.eventAt) return null;

  return {
    key: buildTradeNoticeKey({
      proposalId: detail.tradeProposalId,
      eventKind: detail.event,
      eventAt: detail.eventAt,
    }),
    proposalId: detail.tradeProposalId,
    chatMessageId: detail.chatMessageId,
    eventKind: detail.event,
    title: getTradeEventLabel(detail.event) || "Trade update",
    message: detail.message || `${detail.ticker || "Trade"} updated.`,
    severity: getTradeEventSeverity(detail.event),
    eventAtMs: Date.parse(detail.eventAt),
  };
};

export const getStatsWindowStart = (windowKey) => {
  const now = Date.now();
  if (windowKey === "7d") return now - (7 * ONE_DAY_MS);
  if (windowKey === "30d") return now - (30 * ONE_DAY_MS);
  return null;
};

export const sortTradeProposals = (tradeProposals) => {
  if (!Array.isArray(tradeProposals)) return [];
  return [...tradeProposals].sort((a, b) => {
    const statusOrderDelta = getStatusSortOrder(a) - getStatusSortOrder(b);
    if (statusOrderDelta !== 0) return statusOrderDelta;

    return getProposalSortTimestamp(b) - getProposalSortTimestamp(a);
  });
};

export const partitionTradeProposalsByStatus = (sortedTradeProposals) => {
  const proposals = Array.isArray(sortedTradeProposals) ? sortedTradeProposals : [];
  const pendingTradeProposals = proposals.filter(
    (proposal) => getNormalizedStatus(proposal) === "pending_activation",
  );
  const activeTradeProposals = proposals.filter(
    (proposal) => getNormalizedStatus(proposal) === "active",
  );
  const completedTradeProposals = proposals.filter(
    (proposal) => getNormalizedStatus(proposal) === "closed",
  );

  return {
    pendingTradeProposals,
    activeTradeProposals,
    completedTradeProposals,
    openTradeProposals: [...pendingTradeProposals, ...activeTradeProposals],
  };
};

export const buildTradeRows = ({
  pendingTradeProposals = [],
  activeTradeProposals = [],
  completedTradeProposals = [],
}) => {
  const rows = [];

  const appendSection = (kind, title, proposalsForSection) => {
    if (proposalsForSection.length === 0) return;

    rows.push({ kind: "section", title, key: `${kind}-heading` });
    proposalsForSection.forEach((proposal, index) => {
      rows.push({
        kind: "proposal",
        proposal,
        key: `proposal-${kind}-${proposal.id ?? proposal.chatMessageId ?? getProposalSortTimestamp(proposal)}-${index}`,
      });
    });
  };

  appendSection("pending", "Pending Activation", pendingTradeProposals);
  appendSection("active", "Active Trades", activeTradeProposals);
  appendSection("completed", "Completed Trades", completedTradeProposals);

  return rows;
};

export const filterClosedTradesForStats = (completedTradeProposals, statsWindow) => {
  const start = getStatsWindowStart(statsWindow);
  return (Array.isArray(completedTradeProposals) ? completedTradeProposals : []).filter(
    (proposal) => {
      if (start == null) return true;
      const closedAt = getProposalClosedTimestamp(proposal);
      return Number.isFinite(closedAt) && closedAt >= start;
    },
  );
};

export const computeTradeStats = (closedTradesForStats) => {
  const trades = Array.isArray(closedTradesForStats) ? closedTradesForStats : [];
  const pnlValues = trades
    .map((proposal) => getProposalPercentChange(proposal))
    .filter((value) => Number.isFinite(value));

  const measurableCount = pnlValues.length;
  const closedCount = trades.length;
  const winners = pnlValues.filter((value) => value > 0);
  const losers = pnlValues.filter((value) => value < 0);
  const sum = pnlValues.reduce((acc, value) => acc + value, 0);
  const grossProfit = winners.reduce((acc, value) => acc + value, 0);
  const grossLoss = losers.reduce((acc, value) => acc + value, 0);
  const grossLossAbs = Math.abs(grossLoss);
  const averageReturn = measurableCount > 0 ? sum / measurableCount : null;
  const averageWin = winners.length > 0
    ? grossProfit / winners.length
    : null;
  const averageLoss = losers.length > 0
    ? grossLoss / losers.length
    : null;
  const winRate = measurableCount > 0
    ? (winners.length / measurableCount) * 100
    : null;
  let profitFactor = null;
  if (grossLossAbs > 0) {
    profitFactor = grossProfit / grossLossAbs;
  } else if (grossProfit > 0) {
    profitFactor = Number.POSITIVE_INFINITY;
  }

  return {
    closedCount,
    measurableCount,
    averageReturn,
    averageWin,
    averageLoss,
    winRate,
    profitFactor,
    expectancy: averageReturn,
  };
};

export const buildTradeStatsItems = (tradeStats) => {
  const formatRatio = (value) => {
    if (value == null) return "—";
    if (value === Number.POSITIVE_INFINITY) return "∞";
    return value.toFixed(2);
  };

  return [
    { label: "Closed", value: `${tradeStats.closedCount}` },
    {
      label: "Measured",
      value: `${tradeStats.measurableCount}`,
    },
    {
      label: "Win Rate",
      value: formatPercent(tradeStats.winRate) || "—",
    },
    {
      label: "Avg Return",
      value: formatSignedPercent(tradeStats.averageReturn) || "—",
    },
    {
      label: "Avg Win",
      value: formatSignedPercent(tradeStats.averageWin) || "—",
    },
    {
      label: "Avg Loss",
      value: formatSignedPercent(tradeStats.averageLoss) || "—",
    },
    {
      label: "Profit Factor",
      value: formatRatio(tradeStats.profitFactor),
    },
    {
      label: "EV / Trade",
      value: formatSignedPercent(tradeStats.expectancy) || "—",
    },
  ];
};
